import React from "react";
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  FormControl,
  FormLabel,
  Heading,
  SimpleGrid,
  Text,
  Input,
  InputGroup,
  InputLeftAddon,
  InputRightAddon,
} from "@chakra-ui/react";

const amount = (value) => {
  const number = Number(value);
  return Number.isFinite(number) && number >= 0 ? number : 0;
};

const rawAmount = (value) => {
  const cleaned = String(value ?? "")
    .replace(/[^\d.]/g, "")
    .replace(/(\..*)\./g, "$1");
  const [integer = "", decimals] = cleaned.split(".");
  const normalizedInteger =
    integer.replace(/^0+(?=\d)/, "") || (cleaned ? "0" : "");
  return decimals === undefined
    ? normalizedInteger
    : `${normalizedInteger}.${decimals.slice(0, 2)}`;
};

const formattedAmount = (value) => {
  const raw = rawAmount(value);
  if (!raw) return "";
  const [integer, decimals] = raw.split(".");
  const grouped = integer.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return decimals === undefined ? grouped : `${grouped}.${decimals}`;
};

export const MoneyInput = ({ value, onChange, isDisabled }) => (
  <InputGroup>
    <InputLeftAddon>₦</InputLeftAddon>
    <Input
      type="text"
      inputMode="decimal"
      value={formattedAmount(value)}
      isDisabled={isDisabled}
      onChange={(event) => onChange(rawAmount(event.target.value))}
      aria-label="Amount in Nigerian naira"
    />
  </InputGroup>
);

export const emptyPayment = {
  fullAmount: 0,
  discount: 0,
  discountType: "percentage",
  totalAmount: 0,
  paid: false,
};

const PaymentSection = ({
  value = emptyPayment,
  onChange,
  readOnly = false,
  derivedFromItems = false,
}) => {
  const confirmed = Boolean(value.paid && value.markedPaidBy);
  const locked = readOnly || confirmed;
  const updateAmount = (field, nextValue) => {
    const next = { ...value, [field]: nextValue };
    if (field === "fullAmount" || field === "discount") {
      next.totalAmount =
        Math.round(
          Math.max(
            amount(next.fullAmount) -
              (amount(next.fullAmount) * amount(next.discount)) / 100,
            0,
          ) * 100,
        ) / 100;
    }
    onChange?.(next);
  };

  return (
    <Box
      mt="5"
      mb="5"
      borderWidth="1px"
      borderColor={value.paid ? "green.200" : "gray.200"}
      borderRadius="xl"
      overflow="hidden"
    >
      <Box
        px="4"
        py="3"
        bg={value.paid ? "green.50" : "gray.50"}
        borderBottomWidth="1px"
      >
        <Heading size="sm">Payment details</Heading>
        <Text mt="1" fontSize="xs" color="gray.500">
          Amounts are recorded in Nigerian naira (₦).
        </Text>
      </Box>
      <Box p="4">
        {confirmed && (
          <Alert status="success" borderRadius="lg" mb="4">
            <AlertIcon />
            <Box>
              <Text fontWeight="800">Payment confirmed</Text>
              <Text fontSize="sm">
                Payment fields are permanently locked after confirmation.
              </Text>
            </Box>
          </Alert>
        )}
        {derivedFromItems && (
          <Text mb="4" fontSize="sm" color="gray.600">
            Medication discounts are applied per item. These totals update
            automatically.
          </Text>
        )}
        <SimpleGrid columns={{ base: 1, md: derivedFromItems ? 2 : 3 }} gap="4">
          <FormControl isRequired>
            <FormLabel fontSize="sm">Full amount</FormLabel>
            <MoneyInput
              value={value.fullAmount ?? 0}
              isDisabled={locked}
              onChange={(nextValue) => updateAmount("fullAmount", nextValue)}
            />
          </FormControl>
          {!derivedFromItems && (
            <FormControl>
              <FormLabel fontSize="sm">Discount percentage</FormLabel>
              <InputGroup>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  value={value.discount ?? 0}
                  isDisabled={locked}
                  onChange={(event) =>
                    updateAmount("discount", event.target.value)
                  }
                />
                <InputRightAddon>%</InputRightAddon>
              </InputGroup>
            </FormControl>
          )}
          <FormControl isRequired>
            <FormLabel fontSize="sm">Total amount to be paid</FormLabel>
            <MoneyInput
              value={value.totalAmount ?? 0}
              isDisabled={locked}
              onChange={(nextValue) =>
                onChange?.({ ...value, totalAmount: nextValue })
              }
            />
            {!locked && (
              <Text mt="1" fontSize="xs" color="gray.500">
                Calculated automatically, but you can adjust it.
              </Text>
            )}
          </FormControl>
        </SimpleGrid>
        {value.markedPaidBy && (
          <Text mt="3" fontSize="sm" color="gray.600">
            Confirmed by{" "}
            <Badge colorScheme="green">
              {`${value.markedPaidBy.firstName || ""} ${value.markedPaidBy.lastName || ""}`.trim() ||
                "Staff member"}
            </Badge>
            {value.markedPaidAt
              ? ` on ${new Date(value.markedPaidAt).toLocaleString()}`
              : ""}
          </Text>
        )}
      </Box>
    </Box>
  );
};

export default PaymentSection;
